<template>
  <div class="status-board">
    <!-- 页头：标题 + 操作 -->
    <div class="page-header">
      <div class="ph-left">
        <h2 style="margin-top: 0;">
          开票主体状态监控看板
          <span class="h2-pt">Painel de Monitoramento de Status de Entidades Emissoras</span>
        </h2>
        <div class="desc">
          <span>实时展示 {{ states.length }} 个已服务州的税务资质 + 系统配置 双轨健康度</span>
          <span class="desc-pt">
            Exibe em tempo real a saúde das duas trilhas (qualificação fiscal + configuração do sistema) dos
            {{ states.length }} estados atendidos
          </span>
        </div>
      </div>
    </div>

    <!-- KPI -->
    <kpi-cards
      :states="states"
      :alert-today="alertToday"
      :schedule="schedule"
      :service-total="serviceTotal"
      :summary="kpi"
      @jump="handleJump"
    />

    <!-- 覆盖率口径说明（A07） -->
    <div class="alert alert-warn">
      <svg
        class="ico"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 7.4v5.6" />
        <circle
          cx="12"
          cy="16.6"
          r="1.15"
          fill="currentColor"
          stroke="none"
        />
      </svg>
      <span class="alert-text">
        <span>北部 {{ unmonitoredNum }} 州（{{ northText }}）当前未对外报价，本次不纳入监控范围。如需扩展监控，请联系产品经理评估。</span>
        <span class="alert-pt">Os {{ unmonitoredNum }} estados do Norte ({{ northText }}) não têm tarifas publicadas e ficam fora do monitoramento. Para expandir a cobertura, contate o gerente de produto.</span>
      </span>
    </div>

    <div class="board-grid">
      <!-- 左：巴西 27 州双轨健康度地图 -->
      <div class="card card-left">
        <div class="card-header">
          <h3>
            <span>{{ states.length }} 个已服务州 · 双轨健康度</span>
            <span class="h3-pt">{{ states.length }} estados atendidos · saúde das duas trilhas</span>
          </h3>
          <div class="actions">
            <span class="tag tag-ok"><span class="dot" /><bi-label zh="税务正常" pt="Normal (Fisco)" inline /></span>
            <span class="tag tag-info"><span class="dot" /><bi-label zh="系统已配置" pt="Sistema Configurado" inline /></span>
            <span class="tag tag-err"><span class="dot" /><bi-label zh="异常" pt="Exceção" inline /></span>
          </div>
        </div>
        <div class="card-body">
          <brazil-map
            :states="states"
            :ie-table="ieTable"
            :legend="mapLegend"
            :checking="checking"
            :expire-warn-days="expireWarnDays"
            :unmonitored-num="unmonitoredNum"
            :unmonitored-uf="unmonitoredUf"
            @select="openUfDetail"
          />
        </div>
      </div>

      <!-- 右：实时异常清单 + 最近告警时间线（与左卡等高，内容区内部滚动） -->
      <div class="card card-right">
        <div class="card-fill">
          <div class="card-header">
            <h3>
              <span>实时异常 / 告警</span>
              <span class="h3-pt">Exceções / Alertas em Tempo Real</span>
            </h3>
            <a class="link-more" @click="handleJump({ module: 'alert-log' })">
              <bi-label zh="查看全部" pt="Ver Tudo" inline /> ›
            </a>
          </div>
          <div class="card-fill-body">
            <div class="sub-block">
              <div class="sub-title">
                <span>异常主体清单</span>
                <span class="sub-pt">Lista de Entidades com Exceção</span>
              </div>
              <anomaly-list :list="anomalies" @view="openAnomalyDetail" />
            </div>
            <div class="sub-block">
              <div class="sub-title">
                <span>最近告警时间线</span>
                <span class="sub-pt">Linha do Tempo de Alertas Recentes</span>
              </div>
            </div>
            <alert-timeline :list="timeline" />
          </div>
        </div>
      </div>
    </div>

    <!-- UF 详情抽屉：优先用详情接口返回的 detail，其次回退地图接口数据 -->
    <uf-detail-drawer
      :visible.sync="drawerVisible"
      :uf="drawerUf"
      :detail="drawerDetail"
      :change-log="drawerChangeLog"
      @check="handleCheckOne"
    />
  </div>
</template>

<script>
import BiLabel from './BiLabel';
import KpiCards from './KpiCards';
import BrazilMap from './BrazilMap';
import AnomalyList from './AnomalyList';
import AlertTimeline from './AlertTimeline';
import UfDetailDrawer from './UfDetailDrawer';
import {
  getInvoiceSubjectStatusMonitor,
  getInvoiceSubjectStatusMonitorBrazil,
  getInvoiceSubjectStatusMonitorException,
  getInvoiceSubjectStatusMonitorTimeline,
  getInvoiceSubjectStatusMonitorIEDetail,
  getInvoiceSubjectStatusMonitorStateList,
  getInvoiceSubjectStatusMonitorLogList
} from '@/api/invoiceCenter';

export default {
  name: 'StatusBoard',
  components: { BiLabel, KpiCards, BrazilMap, AnomalyList, AlertTimeline, UfDetailDrawer },
  props: {
    // 到期提醒提前天数：来自告警配置，决定地图"即将过期"图例与 tooltip 的预警窗口
    expireWarnDays: { type: Number, default: 0 }
  },
  data() {
    return {
      // ===== 展示数据：全部来自接口，接口未通 / 请求失败时保持空态，不回退假数据 =====
      states: [],
      ieTable: [],
      anomalies: [],
      timeline: [],
      alertToday: {},
      // 抽屉「近期变更」：打开抽屉时按 UF 查变更日志接口，接口失败时留空
      drawerChangeLog: [],
      schedule: {},
      serviceTotal: 0,
      // KPI 接口（summary）原始返回，传给 KpiCards 优先渲染
      kpi: null,
      // 地图接口的图例统计（legend），预留给地图图例计数使用
      mapLegend: null,
      apiLoading: false,
      // ===== 交互状态 =====
      checking: false,
      drawerVisible: false,
      drawerUf: '',
      // 详情接口返回的单条记录：抽屉首选数据源（地图点击且拿不到 subjectId 时为空，回退地图数据）
      drawerDetail: null,
      unmonitoredNum: 0,
      unmonitoredUf: []
    };
  },
  computed: {
    /** 未纳入监控的北部州拼接文案（如 AC/AM/AP/PA/RO/RR/TO），用于覆盖率口径说明 */
    northText() {
      return this.unmonitoredUf.map(item => item.uf).join('/');
    }
  },
  mounted() {
    // 进入看板即拉一次接口；失败时按空态展示
    this.fetchAll();
  },
  methods: {
    // 获取未监控的州列表
    fetchUnmonitoredUf() {
      getInvoiceSubjectStatusMonitorStateList().then(res => {
        if (res.code === 200) {
          // 提取状态status === 0的州
          this.unmonitoredUf = res.data.filter(item => item.status === 0);
          this.unmonitoredNum = this.unmonitoredUf.length;
        }
      });
    },
    // ============ 接口对接 ============
    /** 并行拉取看板 4 个接口；任一失败不影响其余模块，只打日志 */
    async fetchAll(silent = false) {
      this.apiLoading = true;
      await Promise.all([
        this.fetchSummary(),
        this.fetchMap(),
        this.fetchAnomalies(),
        this.fetchTimeline()
      ]);
      this.fetchUnmonitoredUf();
      this.apiLoading = false;
    },
    /**
     * 供父级调用：IE 管理「全部检查」完成后在后台静默刷新看板
     * 覆盖「上次检查 / 下次定时」时间、各州双轨状态、异常清单与告警时间线（不弹提示）
     */
    refreshBoard() {
      return this.fetchAll(true);
    },
    /** 拉取 KPI（summary）：覆盖 KPI 卡数值、上次/下次检查时间、服务州总数 */
    async fetchSummary() {
      try {
        const res = await getInvoiceSubjectStatusMonitor();
        this.applySummary(res && res.data ? res.data : null);
      } catch (e) {
        console.error('[状态看板] KPI 接口请求失败', e);
      }
    },
    /** 拉取巴西地图：覆盖各州双轨状态与有效期 */
    async fetchMap() {
      try {
        const res = await getInvoiceSubjectStatusMonitorBrazil();
        this.applyMap(res && res.data ? res.data : null);
      } catch (e) {
        console.error('[状态看板] 巴西地图接口请求失败', e);
      }
    },
    /** 拉取异常主体清单 */
    async fetchAnomalies() {
      try {
        const res = await getInvoiceSubjectStatusMonitorException();
        this.applyAnomalies(res && res.data ? res.data : null);
      } catch (e) {
        console.error('[状态看板] 异常主体清单接口请求失败', e);
      }
    },
    /** 拉取告警时间线 */
    async fetchTimeline() {
      try {
        const res = await getInvoiceSubjectStatusMonitorTimeline();
        this.applyTimeline(res && res.data ? res.data : null);
      } catch (e) {
        console.error('[状态看板] 告警时间线接口请求失败', e);
      }
    },

    // ============ 接口数据 → 展示结构 ============
    /** KPI：summary 原样交给 KpiCards 渲染；lastCheck（at / source / nextAt / stale）直接用接口字段 */
    applySummary(data) {
      if (!data) return;
      this.kpi = data;
      if (data.lastCheck) this.schedule = data.lastCheck;
      if (data.coverage && data.coverage.totalUfs) this.serviceTotal = data.coverage.totalUfs;
      // 后端正在执行检查时，保持「全部检查」按钮的 loading；不主动置 false，避免打断本地演示
      if (data.checking && data.checking.running) this.checking = true;
    },
    /**
     * 地图：items 即州状态（字段已与页面一致）
     * 不能按 enabled 过滤：enabled=false 的州接口同样返回了 tax / sys，地图要按状态着色（否则会被误判成「未覆盖」涂灰）
     */
    applyMap(data) {
      if (!data || !Array.isArray(data.items)) return;
      this.states = data.items;
      this.mapLegend = data.legend || null;
    },
    /** 异常清单：接口 data 即为列表，字段与页面展示一致，直接使用 */
    applyAnomalies(list) {
      if (!Array.isArray(list)) return;
      this.anomalies = list;
    },
    /** 告警时间线：items 字段与页面展示一致，直接使用 */
    applyTimeline(data) {
      if (!data || !Array.isArray(data.items)) return;
      this.timeline = data.items;
    },
    /** 单条手动查询（地图州块点击触发）：演示阶段只提示，不真正调接口 */
    handleCheckOne(uf) {
      this.$message.success(`已触发 ${uf} 单条查询`);
    },
    /** 地图州块点击：用该州的 subjectId 调详情接口；subjectId 为空时不进详情，只给提示 */
    openUfDetail(uf) {
      const st = this.states.find(s => s.uf === uf) || {};
      if (!st.subjectId) {
        this.$message.warning(`${uf} 暂无启用主体，无法查看详情 / Sem entidade ativa em ${uf}`);
        return;
      }
      this.fetchDetail(st.subjectId, uf);
    },
    /** 抽屉「近期变更」：按 UF 查变更日志接口取最近 5 条；失败只提示，该区留空 */
    async fetchChangeLog(uf) {
      try {
        const res = await getInvoiceSubjectStatusMonitorLogList({
          pageNumber: 1,
          pageSize: 5,
          // cnpj / ie / uf 均为数组格式（接口 List 入参），未筛选的传空数组
          params: { uf: uf ? [uf] : [], startTimeIso: null, endTimeIso: null, cnpj: [], ie: [], triggerType: null }
        });
        const ok = res && res.code === 200 && res.data && Array.isArray(res.data.items);
        this.drawerChangeLog = ok ? res.data.items : [];
      } catch (e) {
        this.drawerChangeLog = [];
        console.error('[状态看板] 变更日志接口请求失败', e);
      }
    },
    /** 异常主体清单「查看」：用该主体的 subjectId 调详情接口 */
    openAnomalyDetail(item) {
      const subjectId = item && item.subjectId;
      if (!subjectId) {
        this.$message.warning('该异常主体缺少 subjectId，无法查询详情 / Entidade sem subjectId');
        return;
      }
      this.fetchDetail(subjectId, (item && item.uf) || '');
    },
    /**
     * 调「查询主体详情」接口：成功才打开抽屉且只用接口返回的数据；失败只提示
     * @param {number} subjectId 主体 id（详情接口以 id 入参）
     * @param {string} uf 兜底展示的州代码
     */
    async fetchDetail(subjectId, uf) {
      try {
        const res = await getInvoiceSubjectStatusMonitorIEDetail(subjectId);
        const data = res && res.data;
        if (!data) {
          this.$message.error(`${uf || subjectId} 详情接口返回为空 / Detalhes vazios`);
          return;
        }
        this.drawerDetail = data;
        this.drawerUf = data.uf || uf;
        this.drawerVisible = true;
        // 抽屉「近期变更」：按 UF 拉该州最近的变更记录
        this.fetchChangeLog(this.drawerUf);
      } catch (e) {
        console.error('[状态看板] 主体详情接口请求失败', e);
        this.$message.error(`${uf || subjectId} 详情接口请求失败，请稍后重试 / Falha ao consultar detalhes`);
      }
    },
    /** KPI / 链接联动跳转到其他子模块 */
    handleJump(payload) {
      this.$emit('jump', payload);
    }
  }
};
</script>

<style scoped lang="scss">
.status-board {
  padding: 4px 0 20px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
}
.page-header h2 {
  font-size: 18px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
}
.page-header h2 .h2-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
  margin-top: 2px;
}
.page-header .desc {
  color: #86909c;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
}
.page-header .desc .desc-pt {
  font-size: 12px;
  color: #86909c;
}
.page-header .ph-right {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* 覆盖率口径说明 */
.alert {
  padding: 10px 14px;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 12px;
}
.alert-warn {
  background: #fff7e8;
  color: #ad6800;
  border: 1px solid #ffe7ba;
}
.alert .ico {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  flex-shrink: 0;
}
.alert-text {
  display: flex;
  flex-direction: column;
  line-height: 1.6;
}
.alert-text .alert-pt {
  font-size: 12px;
  color: #5c6b7a;
}

/* 左右等宽等高布局：两卡同高，右卡内容区内部滚动 */
.board-grid {
  display: grid;
  grid-template-columns: 1.55fr 1fr;
  gap: 12px;
  align-items: stretch;
}
@media (max-width: 1180px) {
  .board-grid {
    grid-template-columns: 1fr;
  }
}

.card {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  min-width: 0;
}
.card-header {
  padding: 14px 20px;
  border-bottom: 1px solid #f0f1f5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  row-gap: 8px;
}
.card-header h3 {
  font-size: 15px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
}
.card-header h3 .h3-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
}
.card-header .actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}
.card-body {
  padding: 20px;
}
.link-more {
  color: #00a551;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}
.link-more:hover {
  text-decoration: underline;
}

/* 右侧卡片：外层不参与行高计算，高度跟随左侧地图卡，内容超出内部滚动 */
.card-right {
  position: relative;
  overflow: hidden;
  min-height: 560px;
}
.card-fill {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
}
.card-fill-body {
  flex: 1 1 auto;
  min-height: 0;
  padding-bottom: 16px;
}

.sub-block {
  padding: 12px 20px 0;
}
.sub-title {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #86909c;
  margin-bottom: 8px;
}
.sub-title .sub-pt {
  font-size: 12px;
  color: #86909c;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  line-height: 18px;
  white-space: nowrap;
}
.tag .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.tag-ok { background: #e8f7e9; color: #00b42a; }
.tag-err { background: #ffecec; color: #f53f3f; }
.tag-info { background: #e6f0ff; color: #165dff; }

</style>
